import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import TawkMessengerReact from "@tawk.to/tawk-messenger-react"
import { useNavigate, useOutletContext } from "react-router-dom"
import { useMsal, useIsAuthenticated } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"

import Slider from "../../atoms/swiper/Slider.jsx"
import Footer from "../../atoms/footer/index.js"

import SearchBar from "./components/SearchBar"
import MyRequests from "./components/MyRequests"
import AppCategory from "./components/AppCategory"
import OtherAppsDrawer from "./components/OtherAppsDrawer"
import QRModal from "./components/QRModal"
import SapGoLivePopup from "./components/SapGoLivePopup"

import { consumeSapPostLoginRedirect } from "../shared/utils/sapPostLoginRedirect"
import { requestToken } from "../../auth/AuthServices"
import { callMsGraph } from "../../utils/graph.js"
import HeaderFarms from "../../utils/header_farms.js"
import API from "../../services/index.js"

import permitdash from "../../containers/home/asset/permit-dash.png"
import dumper from "../../containers/home/asset/mining-truck-2.png"
import apps from "../../containers/home/asset/apps.png"
import more from "../../containers/home/asset/more.png"

import menuData from "../../containers/home/menu.json"
import "../../containers/home/styles.css"

const Home = ({ setInitializeSession, setInitializeSessionFarms }) => {
  const { accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const tawkMessengerRef = useRef()
  const tawkCustomStyle = useMemo(
    () => ({
      zIndex: 999999,
      visibility: {
        mobile: {
          position: "bl",
          xOffset: "16px",
          yOffset: "64px",
        },
      },
    }),
    []
  )
  const navigate = useNavigate()
  const outletContext = useOutletContext()
  const pendingCount = outletContext?.pendingCount ?? 0

  // State management
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedApp] = useState(null)
  const [_, setSurname] = useState(null)
  const [open, setOpen] = useState(false)
  const [myRequests, setMyRequests] = useState(() => {
    try {
      const cached = localStorage.getItem("my_requests_cache")
      if (!cached) return []
      const { data, ts } = JSON.parse(cached)
      if (Date.now() - ts > 5 * 60 * 1000) return []
      return Array.isArray(data) ? data : []
    } catch {
      return []
    }
  })
  const [query, setQuery] = useState("")
  const [_isSearchFocused] = useState(false)

  // Animated search placeholder state
  const [, setPlaceholderIndex] = useState(0)
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("")
  const [previousPlaceholder, setPreviousPlaceholder] = useState("")
  const [isAnimating, setIsAnimating] = useState(false)
  const animationTimeoutRef = useRef(null)
  const animationDuration = 300

  const placeholderItems = useMemo(() => [...menuData.personal, ...menuData.operasional, ...menuData.support].map(item => item.nickname || item.app_name).filter(Boolean), [])

  // Animated placeholder effect
  useEffect(() => {
    if (placeholderItems.length === 0) return

    setAnimatedPlaceholder(placeholderItems[0])

    const interval = setInterval(() => {
      setPlaceholderIndex(prevIndex => {
        const currentText = placeholderItems[prevIndex]
        const nextIndex = (prevIndex + 1) % placeholderItems.length
        const nextText = placeholderItems[nextIndex]

        setPreviousPlaceholder(currentText)
        setAnimatedPlaceholder(nextText)
        setIsAnimating(true)

        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current)
        }

        animationTimeoutRef.current = setTimeout(() => {
          setIsAnimating(false)
          setPreviousPlaceholder("")
        }, animationDuration)

        return nextIndex
      })
    }, 3000)

    return () => {
      clearInterval(interval)
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current)
      }
    }
  }, [placeholderItems])

  // Menu data filtering
  const OperasionalMenu = menuData.operasional
  const personalMenu = menuData.personal
  const supportMenu = menuData.support
  const sapModuleMenu = menuData.sap_module

  const normalize = (s = "") => (s || "").toString().toLowerCase()
  const matchesQuery = (item, q) => {
    if (!q) return true
    const hay = `${item.app_name || ""} ${item.nickname || ""}`
    return normalize(hay).includes(normalize(q))
  }

  const filteredPersonal = personalMenu.filter(it => matchesQuery(it, query))
  const filteredOperasional = OperasionalMenu.filter(it => matchesQuery(it, query))
  const filteredSupport = supportMenu.filter(it => matchesQuery(it, query))
  const filteredSapModule = sapModuleMenu.filter(it => matchesQuery(it, query))
  const hasAnyResult = filteredPersonal.length + filteredOperasional.length + filteredSupport.length + filteredSapModule.length > 0

  // Handlers
  const handleQueryChange = e => {
    const value = e.target.value
    setQuery(value)
  }

  async function onMenuClick(item) {
    const accessToken = await RequestToken()
    if (accessToken) {
      handleMenuClick(item, accessToken)
    } else {
      console.warn("Gagal mendapatkan token di Menu Click, silakan coba lagi.")
    }
  }

  function handleMenuClick(items, token) {
    if (!token) {
      console.error("Token is undefined!")
      return
    }

    try {
      const sessionData = localStorage.getItem("session_signin")
      const fullname = sessionData ? JSON.parse(sessionData).fullname : "-"
      const employee_id = sessionData ? JSON.parse(sessionData).nik : "-"

      API.menuLog(HeaderFarms(token), {
        employee_id: employee_id,
        app_name: items.app_name,
        sort: items.sort,
        code: items.code,
        fullname: fullname,
      })
    } catch (err) {
      console.error("handleMenuClick failed send data", err)
    }
  }

  const handleTawkLoad = () => {
    const sessionData = localStorage.getItem("session_farms")
    const fullname = sessionData ? JSON.parse(sessionData).fullname : "-"
    const email = sessionData ? JSON.parse(sessionData).email : "-"
    const branch = sessionData ? JSON.parse(sessionData).branch_nickname : "-"

    if (window.Tawk_API && window.Tawk_API.setAttributes) {
      window.Tawk_API.setAttributes(
        {
          name: fullname + " - " + branch,
          email: email,
        },
        function (error) {
          if (error) {
            console.error("Error setting attributes:", error)
          }
        }
      )
    }
  }

  async function RequestToken() {
    const accessToken = await requestToken()

    if (!accessToken) return null

    getMyRequests(accessToken)

    try {
      const response = await callMsGraph(accessToken)
      setSurname(response)

      if (window.location.pathname === "/home") {
        const sapPostLoginRedirect = consumeSapPostLoginRedirect()

        if (sapPostLoginRedirect && sapPostLoginRedirect !== "/home") {
          navigate(sapPostLoginRedirect, { replace: true })
        }
      }

      return accessToken
    } catch (e) {
      console.warn("RequestToken failed", e)
      return accessToken
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const showDrawer = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
  }

  const getMyRequests = accessToken => {
    const today = new Date()
    const lastWeek = new Date()
    lastWeek.setDate(today.getDate() - 7)

    API.MyRequests(HeaderFarms(accessToken), {
      today,
      lastWeek,
    })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : []
        setMyRequests(data)
        try {
          localStorage.setItem("my_requests_cache", JSON.stringify({ data, ts: Date.now() }))
        } catch (e) {
          console.warn("my_requests_cache write failed", e)
        }
      })
      .catch(err => {
        console.error("getMyRequests", err)
      })
  }

  const redirectSlide = async item => {
    if (!item.code) {
      item.code = "IT"
    }

    await onMenuClick(item)

    switch (item.code) {
      case "FRS":
        navigate(`/fms/request/view/${item.form_submit_id}`)
        break
      case "FDS":
        navigate(`/fms/delivery/view/${item.form_submit_id}`)
        break
      case "DST":
        navigate(`/dst/view/${item.form_submit_id}`)
        break
      case "IT":
        navigate(`/help/detail/${item.form_submit_id}`)
        break
      default:
        navigate(`/${item.code}/view/${item.form_submit_id}`)
        break
    }
  }

  // Effects
  useEffect(() => {
    if (inProgress === InteractionStatus.None && accounts.length === 0) {
      const sessionSignin = localStorage.getItem("session_signin")
      const sessionFarms = localStorage.getItem("session_farms")
      const offlineToken = localStorage.getItem("offline_access_token")

      if (sessionSignin && sessionFarms && offlineToken) {
        console.log("Using stored session")
        setInitializeSession(JSON.parse(sessionSignin))
        setInitializeSessionFarms(JSON.parse(sessionFarms))
      }
    }

    if (isAuthenticated) {
      RequestToken()
    }

    // Patent Redirect for domain spesific
    const currentURL = window.location.href
    const url = new URL(currentURL)

    switch (url.hostname) {
      case "fuel.citrapalu.net":
      case "fuel.gorontalominerals.com":
      case "backdaterf.gorontalominerals.com":
      case "backdatedr.gorontalominerals.com":
      case "backdaterf.citrapalu.net":
      case "backdatedr.citrapalu.net":
        return navigate("/fms/menu")
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, isAuthenticated])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = await RequestToken()

        if (!accessToken || !selectedApp) {
          console.warn("fetchData :", "Failed Get token")
          return
        }

        handleMenuClick(selectedApp, accessToken)
      } catch (err) {
        console.error("fetchData :", "Gagal mengambil data:", err)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp, isAuthenticated])

  // Prepare items with special actions
  const enhancedFilteredSupport = filteredSupport.map(item => ({
    ...item,
    onSpecialAction: item.code === "OA" ? showDrawer : undefined,
  }))

  const enhancedFilteredSapModule = filteredSapModule.map(item => ({
    ...item,
    onSpecialAction: item.code === "OA" ? showDrawer : undefined,
  }))

  return (
    <Fragment>
      <TawkMessengerReact propertyId="65e57a0f9131ed19d9748a0b" widgetId="1ho469jul" customStyle={tawkCustomStyle} ref={tawkMessengerRef} onLoad={handleTawkLoad} />
      {/* <Navbar back={false} subtitle="" /> */}

      <div className="container-fluid home_back">
        {!_isSearchFocused && <Slider />}

        <div className="container p-1 white-back-home" style={{ alignSelf: "center" }}>
          {myRequests.length !== 0 && !_isSearchFocused && <MyRequests requests={myRequests} onRequestClick={redirectSlide} />}

          <SearchBar query={query} onChange={handleQueryChange} animatedPlaceholder={animatedPlaceholder} previousPlaceholder={previousPlaceholder} isAnimating={isAnimating} />

          {!hasAnyResult && (
            <div className="row">
              <div className="col text-center no-result-text">Tidak ada aplikasi yang cocok.</div>
            </div>
          )}

          <AppCategory title="SAP Module" items={enhancedFilteredSapModule} pendingList={pendingCount} onMenuClick={onMenuClick} navigate={navigate} more={more} isNew={true} />
          <AppCategory title="Personal" items={filteredPersonal} pendingList={pendingCount} onMenuClick={onMenuClick} navigate={navigate} more={more} />
          <AppCategory title="Operational" items={filteredOperasional} pendingList={pendingCount} onMenuClick={onMenuClick} navigate={navigate} more={more} />
          <AppCategory title="IT Support" items={enhancedFilteredSupport} pendingList={pendingCount} onMenuClick={onMenuClick} navigate={navigate} more={more} />

          <QRModal visible={isModalVisible} onCancel={handleCancel} />
        </div>

        <OtherAppsDrawer open={open} onClose={onClose} navigate={navigate} permitdash={permitdash} apps={apps} dumper={dumper} />
      </div>

      <SapGoLivePopup />
      <Footer />
    </Fragment>
  )
}

export default Home
